// StickyThemeNav — horizontal sticky chip strip with IntersectionObserver
// scroll-spy. Used on /atelier to let visitors jump between series sections
// without scrolling through everything.
//
// Behavior:
//   - Renders one chip per provided section. Clicking a chip smooth-scrolls
//     to that section's anchor.
//   - Sticky to top of viewport (`top-0`) once the user scrolls past it.
//   - Mobile: chip strip overflows horizontally with `overflow-x-auto`,
//     swipeable on touch devices. Desktop: same row, just doesn't overflow.
//   - Active-state highlight tracks which section is currently in view via
//     IntersectionObserver. When multiple sections are visible at once
//     (e.g. user is between two), the TOPMOST-visible one is marked active
//     using the `rootMargin: '-20% 0px -60% 0px'` viewport band — only
//     sections whose top is between 20% and 40% of viewport count.
//
// Props:
//   sections: Array<{slug: string, label: string}>  — order = render order
//   className?: string — additional wrapper classes
//   testId?:   string
//
// Anchor convention: each section in the page MUST have id=`series-${slug}`
// for the click-scroll + scroll-spy to wire correctly. Slugs are kebab-case
// (e.g. 'japan-series', 'kalmykia').

import React, {useEffect, useRef, useState, useCallback} from 'react';
import {cn} from '@/lib/utils';

export const StickyThemeNav = ({sections, className, testId = 'atelier-theme-nav'}) => {
    const [activeSlug, setActiveSlug] = useState(sections[0]?.slug || null);
    const scrollRef = useRef(null);

    // ─── Scroll-spy via IntersectionObserver ────────────────────────────
    //
    // We observe each `[id="series-<slug>"]` element. The rootMargin band
    // `'-20% 0px -60% 0px'` defines the activation window: a section
    // becomes "active" when its top crosses ~20% of the viewport. This
    // matches typical editorial-site scroll-spy UX (active section feels
    // anchored just above the fold rather than at the very top).
    //
    // When multiple sections satisfy the predicate (which can happen
    // briefly during scrolling), we pick the one with the smallest
    // `boundingClientRect.top` — i.e. the topmost section currently
    // inside the activation band.
    useEffect(() => {
        if (typeof window === 'undefined' || !sections?.length) return undefined;
        const targets = sections
            .map((s) => document.getElementById(`series-${s.slug}`))
            .filter(Boolean);
        if (targets.length === 0) return undefined;

        let visibleMap = new Map(); // slug → boundingClientRect.top

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const slug = entry.target.id.replace(/^series-/, '');
                    if (entry.isIntersecting) {
                        visibleMap.set(slug, entry.boundingClientRect.top);
                    } else {
                        visibleMap.delete(slug);
                    }
                });
                if (visibleMap.size === 0) return;
                // Pick topmost visible (smallest top, considering negative tops as above-band)
                const sorted = [...visibleMap.entries()].sort((a, b) => a[1] - b[1]);
                setActiveSlug(sorted[0][0]);
            },
            {rootMargin: '-20% 0px -60% 0px', threshold: 0},
        );

        targets.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [sections]);

    // ─── Click handler: smooth-scroll to the section anchor ─────────────
    //
    // Native `scrollIntoView({behavior: 'smooth', block: 'start'})` handles
    // both desktop and mobile cleanly. Adds a small top offset (matches
    // section `scroll-mt-24` so the heading sits below the sticky nav).
    const handleChipClick = useCallback((slug) => (e) => {
        e.preventDefault();
        const target = document.getElementById(`series-${slug}`);
        if (!target) return;
        target.scrollIntoView({behavior: 'smooth', block: 'start'});
        // Optimistically set active so the chip highlight responds immediately
        // (the IO will catch up once scroll settles).
        setActiveSlug(slug);
    }, []);

    // ─── Auto-scroll the chip strip to keep active chip in view (mobile) ─
    //
    // On mobile the chip strip overflows horizontally. When a chip becomes
    // active off-screen (because the user scrolled the PAGE past it), we
    // scroll the chip strip so the active chip is visible. Pure UX polish.
    useEffect(() => {
        if (!scrollRef.current || !activeSlug) return;
        const activeEl = scrollRef.current.querySelector(`[data-slug="${activeSlug}"]`);
        if (!activeEl) return;
        // `scrollIntoView` on the chip will scroll its NEAREST scroll container
        // (the chip strip), not the document — exactly what we want.
        activeEl.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
    }, [activeSlug]);

    if (!sections?.length) return null;

    return (
        <div
            className={cn(
                'sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-3',
                // Semi-transparent background + backdrop-blur — keeps the
                // image grid visible behind the nav as the user scrolls.
                'bg-background/85 backdrop-blur-md border-b border-border',
                className,
            )}
            data-testid={testId}
        >
            <div
                ref={scrollRef}
                className="overflow-x-auto scrollbar-none -mx-1"
                style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}
            >
                <div className="flex items-center gap-2 px-1 whitespace-nowrap">
                    {sections.map((s) => {
                        const isActive = s.slug === activeSlug;
                        return (
                            <a
                                key={s.slug}
                                href={`#series-${s.slug}`}
                                data-slug={s.slug}
                                data-testid={`theme-nav-chip-${s.slug}`}
                                data-active={isActive ? 'true' : 'false'}
                                onClick={handleChipClick(s.slug)}
                                className={cn(
                                    'inline-flex items-center h-8 px-3.5 rounded-full',
                                    'text-[11px] tracking-[0.16em] uppercase font-medium',
                                    'border transition-colors',
                                    isActive
                                        ? 'border-foreground bg-foreground text-background'
                                        : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                                )}
                            >
                                {s.label}
                            </a>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default StickyThemeNav;
